import { alpha } from "@mui/material/styles";

/* eslint-disable no-inline-colors/no-inline-colors -- This file defines central admin component theme overrides and intentionally owns fixed color literals where palette tokens are not exposed. */

interface AdminThemeFoundation {
  white: string;
  purple: string;
  grey: string;
  grey50: string;
  shadowBeige: string;
}

interface AdminThemeBackgroundTokens {
  peach: string;
}

interface AdminThemePalette {
  primary: { main: string };
  secondary: { main: string };
  success: { main: string };
  warning: { main: string };
  error: { main: string };
  background: {
    default: string;
    paper: string;
  };
  text: {
    primary: string;
    secondary: string;
  };
  divider: string;
}

interface BuildAdminThemeComponentsArgs {
  isLight: boolean;
  foundation: AdminThemeFoundation;
  backgroundTokens: AdminThemeBackgroundTokens;
  palette: AdminThemePalette;
  typographyFontFamily: string;
}

export const buildAdminThemeComponents = (
  args: BuildAdminThemeComponentsArgs,
) => ({
  MuiCssBaseline: {
    styleOverrides: {
      ":root": {
        "--hellocashflow-font-family": args.typographyFontFamily,
        "--hellocashflow-palette-primary-main": args.palette.primary.main,
        "--hellocashflow-palette-secondary-main": args.palette.secondary.main,
        "--hellocashflow-palette-success-main": args.palette.success.main,
        "--hellocashflow-palette-warning-main": args.palette.warning.main,
        "--hellocashflow-palette-error-main": args.palette.error.main,
        "--hellocashflow-palette-background-default":
          args.palette.background.default,
        "--hellocashflow-palette-background-paper":
          args.palette.background.paper,
        "--hellocashflow-palette-divider": String(args.palette.divider),
        "--hellocashflow-report-cardShadow": args.foundation.shadowBeige,
        "--hellocashflow-radius-button": "8px",
        "--hellocashflow-radius-surface": "12px",
      },
      html: {
        backgroundColor: "var(--hellocashflow-palette-background-default)",
      },
      body: {
        fontFamily: "var(--hellocashflow-font-family)",
        backgroundColor: "var(--hellocashflow-palette-background-default)",
        minHeight: "100vh",
        overflowX: "hidden",
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: args.isLight ? "#ffffff" : "#1e1e1e",
        color: args.isLight ? args.foundation.purple : args.foundation.white,
        boxShadow: "none",
        borderBottom: `1px solid ${
          args.isLight
            ? args.foundation.grey50
            : alpha(args.foundation.white, 0.12)
        }`,
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: "none",
        fontWeight: 500,
        borderRadius: "var(--hellocashflow-radius-button)",
        fontSize: "0.95rem",
        variants: [
          {
            props: { variant: "contained", color: "primary" },
            style: {
              padding: "12px 16px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: args.foundation.white,
                color: args.foundation.purple,
                border: `2px solid ${args.foundation.purple}`,
                padding: "10px 14px",
                boxShadow: "none",
              },
              "& .MuiSvgIcon-root": {
                width: 24,
                height: 24,
                margin: "-4px 0",
              },
            },
          },
          {
            props: { variant: "outlined", color: "primary" },
            style: {
              // Keep outlined actions readable on dark surfaces; branded purple
              // only has sufficient contrast against light backgrounds.
              color: args.isLight
                ? args.foundation.purple
                : args.palette.text.primary,
              borderColor: args.isLight
                ? args.foundation.purple
                : alpha(args.palette.text.primary, 0.36),
              borderWidth: 2,
              padding: "8px 16px",
              "&:hover": {
                backgroundColor: args.isLight
                  ? args.foundation.purple
                  : alpha(args.palette.text.primary, 0.12),
                color: args.foundation.white,
                border: args.isLight
                  ? "2px solid transparent"
                  : `2px solid ${alpha(args.palette.text.primary, 0.56)}`,
              },
              "& .MuiSvgIcon-root": {
                width: 24,
                height: 24,
                margin: "-4px 0",
              },
            },
          },
          {
            props: { variant: "text", color: "primary" },
            style: {
              padding: 0,
              minWidth: 0,
            },
          },
        ],
      },
    },
    variants: [
      {
        props: { variant: "ghost" },
        style: {
          color: args.palette.text.secondary,
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: alpha(
              args.palette.text.primary,
              args.isLight ? 0.06 : 0.12,
            ),
          },
        },
      },
    ],
  },
  MuiCard: {
    styleOverrides: {
      root: {
        boxShadow: args.isLight
          ? `0px 0px 20px 0px ${args.foundation.shadowBeige}`
          : "0 1px 12px rgba(0, 0, 0, 0.4)",
        borderRadius: "var(--hellocashflow-radius-surface)",
        border: `1px solid ${
          args.isLight
            ? args.foundation.grey50
            : alpha(args.foundation.white, 0.12)
        }`,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: "var(--hellocashflow-radius-surface)",
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        fontWeight: 500,
        borderRadius: 999,
      },
    },
    variants: [
      {
        props: { variant: "status" },
        style: {
          fontWeight: 600,
          fontSize: "0.75rem",
          textTransform: "uppercase",
          letterSpacing: "0.02em",
        },
      },
    ],
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        background: args.foundation.purple,
        color: args.foundation.white,
        borderRadius: "0px 16px 16px 0px",
        fontSize: "16px",
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        color: args.isLight
          ? args.foundation.grey
          : alpha(args.foundation.white, 0.72),
        textDecorationColor: args.isLight
          ? args.foundation.grey
          : alpha(args.foundation.white, 0.72),
        "&:hover": {
          color: args.palette.primary.main,
          textDecorationColor: args.palette.primary.main,
        },
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: "14px",
        color: args.isLight ? args.foundation.purple : args.foundation.white,
        padding: "8px",
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        backgroundColor: args.palette.background.paper,
        padding: "0 4px",
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      input: {
        "&:not(.MuiInputBase-inputMultiline)": {
          height: "1.5em",
          lineHeight: "1.5em",
        },
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        backgroundColor: args.isLight
          ? args.backgroundTokens.peach
          : alpha(args.foundation.white, 0.06),
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        fontWeight: 600,
        color: args.isLight
          ? args.foundation.purple
          : alpha(args.foundation.white, 0.9),
        borderBottom: `1px solid ${
          args.isLight
            ? args.foundation.grey50
            : alpha(args.foundation.white, 0.12)
        }`,
      },
      body: {
        borderBottom: `1px solid ${
          args.isLight
            ? alpha(args.foundation.purple, 0.08)
            : alpha(args.foundation.white, 0.12)
        }`,
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          borderRadius: "var(--hellocashflow-radius-button)",
          "& fieldset": {
            borderColor: alpha(args.foundation.purple, 0.25),
          },
          "&:hover fieldset": {
            borderColor: alpha(args.foundation.purple, 0.45),
          },
          "&.Mui-focused fieldset": {
            borderColor: args.foundation.purple,
            borderWidth: "2px",
          },
        },
      },
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: {
        border: `1px solid ${
          args.isLight
            ? alpha(args.foundation.purple, 0.12)
            : alpha(args.foundation.white, 0.12)
        }`,
        borderRadius: "var(--hellocashflow-radius-surface)",
        backgroundColor: args.palette.background.paper,
        overflow: "hidden",
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: args.isLight
            ? args.backgroundTokens.peach
            : alpha(args.foundation.white, 0.06),
          borderBottom: `1px solid ${
            args.isLight
              ? alpha(args.foundation.purple, 0.12)
              : alpha(args.foundation.white, 0.12)
          }`,
        },
        "& .MuiDataGrid-cell": {
          borderBottom: `1px solid ${
            args.isLight
              ? alpha(args.foundation.purple, 0.08)
              : alpha(args.foundation.white, 0.12)
          }`,
        },
        "& .MuiDataGrid-footerContainer": {
          borderTop: `1px solid ${
            args.isLight
              ? alpha(args.foundation.purple, 0.12)
              : alpha(args.foundation.white, 0.12)
          }`,
        },
      },
    },
  },
});
